import { useEffect, useRef, useState } from 'react';
import SearchBar from './SearchBar.jsx';
import { isMobileLayout } from '../utils/isMobile.js';
import { ATLAS } from '../data/atlasConfig.js';

// Top-left breadcrumb. Shows navigation context when zoomed into a region or bereich.

export default function ModeSwitcher({ mode, onBack, backLabel, producersLayer, geoLayer, overlaySelection, showOutOfScope, toggleOutOfScope, satellite, terrain, frankenMap, lagenAvailable, climateLayer, flurbLayer, flurbAvailable, rlpLagenLayer, rlpLagenAvailable, precipAvailable, onSelectView, onAbout, onStudy, onTrip, searchOpen, onOpenSearch, onCloseSearch, onSearchResult, user, onRefresh }) {
  // Hooks must run unconditionally on every render — keep them above the
  // early returns or React's hook order breaks when mode.kind changes.
  const [layersOpen, setLayersOpen] = useState(false);
  const layersRef = useRef(null);
  // Desktop: the search bar is open by default in every view but can be
  // collapsed to a magnifier. Re-opens by default on each view change.
  const [searchHidden, setSearchHidden] = useState(false);

  useEffect(() => {
    setSearchHidden(false);
  }, [mode.kind, mode.name]);

  useEffect(() => {
    if (!layersOpen) return;
    const handler = (e) => {
      if (layersRef.current && !layersRef.current.contains(e.target)) setLayersOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [layersOpen]);

  // Standard stacked-layers icon, shared by both dropdown triggers
  const layersIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );

  // View picker: Map / Satellite / Terrain basemaps + Temp / Sun climate
  // overlays, one exclusive selection. Rendered at the bottom of the full
  // Layers dropdown in region mode, and as the sole content elsewhere.
  // Key Producers and Rivers & Mtns join as cards on the overview only.
  const activeView = (producersLayer && mode.kind === 'regions') ? 'producers'
    : (geoLayer && mode.kind === 'regions') ? 'geo'
    : rlpLagenLayer ? 'rlplagen' : flurbLayer ? 'flurb' : climateLayer ?? (satellite ? 'satellite' : terrain ? 'terrain' : frankenMap ? 'lagen' : 'map');
  const tiles = [
    { key: 'map', label: 'Map', img: '/map-thumb.png' },
    ...(ATLAS.features.satellite ? [{ key: 'satellite', label: 'Satellite', img: '/sat-thumb.jpg' }] : []),
    ...(ATLAS.features.terrain ? [{ key: 'terrain', label: 'Terrain', img: '/climate/terrain-thumb.png' }] : []),
    // Official Lagen map: logged-in users in the Franken region only
    ...(lagenAvailable ? [{ key: 'lagen', label: 'Lagen', img: '/lagen-thumb.png' }] : []),
    ...(ATLAS.features.temp ? [{ key: 'temp', label: 'Temp', img: '/climate/temp-thumb.png' }] : []),
    ...(ATLAS.features.sun ? [{ key: 'sun', label: 'Sun', img: '/climate/sun-thumb.png' }] : []),
    // Monthly precipitation is per-region; only offered where generated
    ...(precipAvailable ? [{ key: 'precip', label: 'Rain', img: '/climate/precip-thumb.png' }] : []),
    // Every producer nationwide over the grey backdrop — overview only
    ...(mode.kind === 'regions' && ATLAS.features.producers ? [{ key: 'producers', label: 'Key Producers', img: '/producers-thumb.png' }] : []),
    // Rivers highlighted from the tileset + mountain ranges — overview only
    ...(mode.kind === 'regions' && ATLAS.features.geo ? [{ key: 'geo', label: 'Key Rivers & Mtns', img: '/geo-thumb.png' }] : []),
    // Dataset is RLP-only: the tile exists only inside its six regions.
    ...(flurbAvailable ? [{ key: 'flurb', label: 'Flurbereinigung', img: '/flurb-thumb.png' }] : []),
    // RLP Weinbergsrolle Einzellagen: signed-in + local bridge only.
    ...(rlpLagenAvailable ? [{ key: 'rlplagen', label: 'RLP Lagen', img: '/lagen-thumb.png' }] : []),
  ];
  const basemapPicker = (
    <div className="layers-dropdown__basemap">
      {tiles.map(({ key, label, img }, i) => {
        // an odd tile count leaves the last tile alone on its row: center it
        const center = i === tiles.length - 1 && tiles.length % 2 === 1;
        return (
          <button
            key={key}
            className={`basemap-tile ${activeView === key ? 'basemap-tile--active' : ''} ${center ? 'basemap-tile--center' : ''}`}
            onClick={() => {
              onSelectView(key);
              // mobile: close on pick — the open menu neither repaints the
              // active border reliably nor survives the trigger button
              // growing (longer flag text shifts the anchored menu)
              if (isMobileLayout()) setLayersOpen(false);
            }}
          >
            <span className="basemap-tile__img" style={{ backgroundImage: `url(${img})` }} />
            <span className="basemap-tile__label">{label}</span>
          </button>
        );
      })}
    </div>
  );

  // Layers button with a state flag bleeding to the right showing the current
  // view; the whole thing opens the menu. Shared by both dropdown variants.
  const VIEW_LABELS = { map: 'Map', satellite: 'Satellite', terrain: 'Terrain', lagen: 'Lagen', temp: 'Temp', sun: 'Sun', precip: 'Rain', producers: 'Key Producers', geo: 'Rivers & Mtns', flurb: 'Flurbereinigung', rlplagen: 'RLP Lagen' };
  const layersTrigger = (
    <button className="mode-btn mode-btn--layers" onClick={() => setLayersOpen(o => !o)} title="Layers" aria-label={`Layers: ${VIEW_LABELS[activeView] ?? 'Map'}`}>
      {layersIcon}
      <span className="mode-btn__layer-flag">{VIEW_LABELS[activeView] ?? 'Map'}</span>
    </button>
  );

  // Layers dropdown for views without the layer toggles (regions overview,
  // unassigned): same trigger, menu holds only the basemap picker.
  const basemapOnlyDropdown = (
    <div className="layers-dropdown" ref={layersRef}>
      {layersTrigger}
      {layersOpen && (
        <div className="layers-dropdown__menu layers-dropdown__menu--basemap-only">
          {basemapPicker}
        </div>
      )}
    </div>
  );

  // Manual data refresh: refetches the current view and (signed in)
  // rebuilds the dot-derived region blobs server-side. Shown on the regions
  // overview and in region views.
  const refreshBtn = user ? (
    <button className="mode-btn mode-btn--refresh" onClick={onRefresh} title="Refresh map data">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2v6h-6" />
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
        <path d="M3 22v-6h6" />
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      </svg>
    </button>
  ) : null;

  // Overview layout shared by every view: open search bar on the left,
  // then the layers dropdown; the trailing slot holds the info button on
  // the overview and the back arrow everywhere else.
  const searchBar = (
    <SearchBar
      onSelectResult={onSearchResult}
      onClose={() => { onCloseSearch(); setSearchHidden(true); }}
      autoFocus={searchOpen}
      staticFirst={!user}
    />
  );
  const searchCollapsedBtn = (
    <button className="mode-btn mode-btn--search-regions" onClick={() => { setSearchHidden(false); onOpenSearch(); }} title="Search">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </button>
  );
  const modesClass = `modes ${searchOpen ? 'modes--search-open' : ''} ${searchHidden && !searchOpen ? 'modes--search-collapsed' : ''}`;
  // Icon-only back arrow in the info button's slot, search-bar height
  const backBtn = (onBack, label) => (
    <button className="mode-btn mode-btn--back" onClick={onBack} title={label} aria-label={label}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
      </svg>
    </button>
  );

  if (mode.kind === 'regions') {
    return (
      <div className={`${modesClass} ${(producersLayer || geoLayer || overlaySelection) ? 'modes--back' : ''}`}>
        {searchBar}
        {searchCollapsedBtn}
        {basemapOnlyDropdown}
        {/* Study Mode: flash cards from app data — signed-in only */}
        {onStudy && (
          <button className="mode-btn mode-btn--study" onClick={onStudy} title="Study Mode" aria-label="Study Mode">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 9.5 12 4.5 2 9.5l10 5 10-5z" />
              <path d="M6 11.8V17c3 2.6 9 2.6 12 0v-5.2" />
            </svg>
          </button>
        )}
        {refreshBtn}
        {/* Key Producers / Rivers & Mtns active: the trailing slot swaps
            info for a back arrow that dismisses the layer */}
        {(producersLayer || geoLayer || overlaySelection) ? backBtn(onBack, backLabel) : (
          <button className="mode-btn mode-btn--info" onClick={onAbout} title="About this app">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  if (mode.kind === 'unassigned') {
    return (
      <div className={`${modesClass} modes--back`}>
        {searchBar}
        {searchCollapsedBtn}
        {basemapOnlyDropdown}
        {/* Study Mode: flash cards from app data — signed-in only */}
        {onStudy && (
          <button className="mode-btn mode-btn--study" onClick={onStudy} title="Study Mode" aria-label="Study Mode">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 9.5 12 4.5 2 9.5l10 5 10-5z" />
              <path d="M6 11.8V17c3 2.6 9 2.6 12 0v-5.2" />
            </svg>
          </button>
        )}
        {refreshBtn}
        {backBtn(onBack, backLabel)}
      </div>
    );
  }

  if (mode.kind !== 'region') return null;

  const toggleButtons = (
    <div className="layers-dropdown" ref={layersRef}>
      {layersTrigger}
      {layersOpen && (
        <div className={`layers-dropdown__menu ${!user ? 'layers-dropdown__menu--basemap-only' : ''}`}>
          {user && (
            <button className="layers-dropdown__item" onClick={toggleOutOfScope}>
              <span className={`toggle-switch ${showOutOfScope ? 'toggle-switch--on' : 'toggle-switch--off'}`} /> <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#fff', border: '1px solid #9ca3af', verticalAlign: 'middle' }} /> Out of Scope
            </button>
          )}
          {basemapPicker}
        </div>
      )}
    </div>
  );


  // Viewing a region — the back target (selection panel, bereich focus,
  // or the overview) is decided by the App-level goBack hierarchy
  return (
    <div className={`${modesClass} modes--back`}>
      {searchBar}
      {searchCollapsedBtn}
      {onTrip && (
        <button className="mode-btn mode-btn--trip" onClick={onTrip} title="Winery Trip Planner" aria-label="Winery Trip Planner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 11 6.6 6.8A2 2 0 0 1 8.5 5.5h7a2 2 0 0 1 1.9 1.3L19 11" />
            <path d="M4 11h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1" />
            <path d="M3 12v4a1 1 0 0 0 1 1h1" />
            <circle cx="7.5" cy="16.5" r="1.7" />
            <circle cx="16.5" cy="16.5" r="1.7" />
            <path d="M9.2 17h5.6" />
          </svg>
        </button>
      )}
      {toggleButtons}
      {refreshBtn}
      {backBtn(onBack, backLabel)}
    </div>
  );
}
